import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Loader2, Sun, Moon, CheckCircle2, XCircle, Clock, Pause, Play, X, Utensils } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, inr, apiError } from "@/lib/api";

const loadScript = (src) => new Promise((res) => {
  if (document.querySelector(`script[src="${src}"]`)) return res(true);
  const s = document.createElement("script"); s.src = src; s.onload = () => res(true); s.onerror = () => res(false); document.body.appendChild(s);
});

export default function MealPlans() {
  const nav = useNavigate();
  const { cart, refresh } = useCart();
  const [plans, setPlans] = useState(null);
  const [subs, setSubs] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [form, setForm] = useState({ meal_type: "lunch", duration_days: 14, start_date: "", address_id: "", payment_method: "cod" });
  const [busy, setBusy] = useState(false);

  const loadSubs = () => api.get("/meal/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/meal/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/addresses").then((r) => { setAddresses(r.data); setForm((f) => ({ ...f, address_id: r.data[0]?.id || "" })); }).catch(() => {});
    loadSubs();
  }, []);

  const subscribe = async () => {
    if (!cart || !cart.items?.length) return toast.error("Add items to your cart first to build a meal plan");
    if (!form.address_id) return toast.error("Select a delivery address");
    setBusy(true);
    try {
      const { data } = await api.post("/meal/subscriptions", form);
      const p = data.payment;
      if (form.payment_method === "cod" || p.gateway === "cod") {
        toast.success("Meal plan activated · Cash on Delivery");
      } else if (p.gateway === "razorpay") {
        const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
        if (!ok || !window.Razorpay) throw new Error("Couldn't load payment gateway");
        await new Promise((resolve, reject) => {
          const rzp = new window.Razorpay({
            key: p.key_id, amount: p.amount, currency: "INR", name: "BiteGoFood By VS-Solutions",
            description: `${form.duration_days}-day ${form.meal_type} plan`, order_id: p.razorpay_order_id,
            handler: async (r) => {
              try { await api.post(`/meal/subscriptions/${data.subscription.id}/verify-payment`, { razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature }); toast.success("Meal plan activated"); resolve(); }
              catch (e) { reject(e); }
            },
            modal: { ondismiss: () => reject(new Error("dismissed")) },
          });
          rzp.open();
        });
      } else {
        await api.post(`/meal/subscriptions/${data.subscription.id}/verify-payment`, { success: true });
        toast.success("Meal plan activated (demo mode)");
      }
      await refresh(); loadSubs();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const act = async (sid, action) => {
    try { await api.post(`/meal/subscriptions/${sid}/action`, { action }); toast.success(`Plan ${action}d`); loadSubs(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const durationDiscount = plans?.durations.find((d) => d.days === Number(form.duration_days))?.discount_pct || 0;
  const daily = cart ? Math.round(cart.food_subtotal * (1 - durationDiscount / 100)) + (cart.delivery_fee || 0) : 0;
  const total = daily * Number(form.duration_days);

  return (
    <div className="min-h-screen bg-surface max-w-[900px] mx-auto pb-24" data-testid="meal-plans">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
        <button onClick={() => nav(-1)} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <h1 className="font-display font-bold text-lg flex items-center gap-2"><CalendarDays size={20} className="text-coral" /> Meal Subscriptions</h1>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Existing subscriptions */}
        {subs.length > 0 && (
          <div data-testid="meal-sub-list">
            <h2 className="font-bold text-ink mb-2">Your meal plans</h2>
            <div className="space-y-2">
              {subs.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl border border-border p-4" data-testid={`meal-sub-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {s.meal_type === "lunch" ? <Sun size={16} className="text-amber-500" /> : <Moon size={16} className="text-indigo-500" />}
                      <span className="font-bold text-ink capitalize">{s.meal_type} · {s.duration_days}-day</span>
                    </div>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${s.status === "active" ? "bg-emerald-100 text-emerald-700" : s.status === "completed" ? "bg-blue-100 text-blue-700" : s.status === "paused" ? "bg-amber-100 text-amber-700" : s.status === "cancelled" ? "bg-gray-100 text-gray-600" : "bg-orange-100 text-orange-700"}`}>{s.status.replace("_", " ")}</span>
                  </div>
                  <div className="text-xs text-ink-600 mt-1">{s.restaurant_name} · {s.start_date} → {s.end_date} · {inr(s.daily_amount)}/day</div>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <Stat icon={CheckCircle2} color="text-emerald-600" label="Delivered" value={s.stats?.delivered || 0} />
                    <Stat icon={Clock} color="text-blue-500" label="Upcoming" value={s.stats?.upcoming || 0} />
                    <Stat icon={XCircle} color="text-red-500" label="Skipped" value={s.stats?.failed || 0} />
                  </div>
                  {(s.deliveries || []).slice(-4).reverse().map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-xs mt-1.5 border-t border-border pt-1.5" data-testid={`meal-delivery-${s.id}-${d.date}`}>
                      <span className="text-ink-600">{d.date}</span>
                      {d.status === "failed" ? <span className="text-red-500 font-semibold">Skipped · {d.reason}</span>
                        : <button onClick={() => d.order_id && nav(`/order/${d.order_id}`)} className="text-coral font-semibold">Track order →</button>}
                    </div>
                  ))}
                  {["active", "paused"].includes(s.status) && (
                    <div className="flex gap-2 mt-3">
                      {s.status === "active" ? <button onClick={() => act(s.id, "pause")} className="flex-1 border border-border rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`meal-pause-${s.id}`}><Pause size={13} /> Pause</button>
                        : <button onClick={() => act(s.id, "resume")} className="flex-1 border border-border rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`meal-resume-${s.id}`}><Play size={13} /> Resume</button>}
                      <button onClick={() => act(s.id, "cancel")} className="flex-1 border border-red-200 text-red-600 rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`meal-cancel-${s.id}`}><X size={13} /> Cancel</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create a plan */}
        <div className="bg-white rounded-2xl border border-border p-4" data-testid="meal-create">
          <h2 className="font-bold text-ink flex items-center gap-2"><Utensils size={17} className="text-coral" /> Build a new meal plan</h2>
          {!cart || !cart.items?.length ? (
            <div className="mt-3 text-center py-6">
              <p className="text-sm text-ink-600">Add your favourite meal to the cart first — we'll deliver it every day of your plan.</p>
              <button onClick={() => nav("/")} className="mt-3 bg-coral text-white font-bold px-4 py-2.5 rounded-xl text-sm" data-testid="meal-browse-btn">Browse restaurants</button>
            </div>
          ) : (
            <>
              <div className="mt-3 bg-surface rounded-xl p-3">
                <div className="text-xs font-bold text-ink-600 uppercase mb-1">Your daily meal · {cart.restaurant?.name}</div>
                {cart.items.map((it) => <div key={it.line_id} className="flex justify-between text-sm py-0.5"><span className="text-ink-600">{it.qty} × {it.name}</span><span>{inr(it.line_total)}</span></div>)}
              </div>

              <div className="mt-3">
                <div className="text-xs font-bold text-ink-600 uppercase mb-1.5">Meal</div>
                <div className="grid grid-cols-2 gap-2">
                  {plans?.meal_types.map((m) => (
                    <button key={m.key} onClick={() => setForm((f) => ({ ...f, meal_type: m.key }))} className={`rounded-xl p-3 border text-left ${form.meal_type === m.key ? "border-coral bg-coral-light" : "border-border"}`} data-testid={`meal-type-${m.key}`}>
                      <div className="font-bold text-sm flex items-center gap-1">{m.key === "lunch" ? <Sun size={14} className="text-amber-500" /> : <Moon size={14} className="text-indigo-500" />} {m.label}</div>
                      <div className="text-[11px] text-ink-600">{m.slot}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3">
                <div className="text-xs font-bold text-ink-600 uppercase mb-1.5">Duration</div>
                <div className="grid grid-cols-3 gap-2">
                  {plans?.durations.map((d) => (
                    <button key={d.days} onClick={() => setForm((f) => ({ ...f, duration_days: d.days }))} className={`rounded-xl p-3 border text-center ${Number(form.duration_days) === d.days ? "border-coral bg-coral-light" : "border-border"}`} data-testid={`meal-duration-${d.days}`}>
                      <div className="font-display font-extrabold text-ink">{d.days}</div>
                      <div className="text-[10px] text-ink-600">days{d.discount_pct ? ` · -${d.discount_pct}%` : ""}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs font-bold text-ink-600 uppercase mb-1.5">Start date</div>
                  <input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} className="w-full bg-surface rounded-xl px-3 py-2.5 text-sm outline-none" data-testid="meal-start-date" />
                </div>
                <div>
                  <div className="text-xs font-bold text-ink-600 uppercase mb-1.5">Address</div>
                  <select value={form.address_id} onChange={(e) => setForm((f) => ({ ...f, address_id: e.target.value }))} className="w-full bg-surface rounded-xl px-3 py-2.5 text-sm outline-none" data-testid="meal-address">
                    {addresses.length ? addresses.map((a) => <option key={a.id} value={a.id}>{a.label} · {a.line1?.slice(0, 20)}</option>) : <option value="">Add an address first</option>}
                  </select>
                </div>
              </div>

              <div className="mt-3">
                <div className="text-xs font-bold text-ink-600 uppercase mb-1.5">Payment</div>
                <div className="grid grid-cols-2 gap-2">
                  {[["cod", "Cash daily (COD)"], ["upi", "Pay once (UPI/Card)"]].map(([k, l]) => (
                    <button key={k} onClick={() => setForm((f) => ({ ...f, payment_method: k }))} className={`rounded-xl p-2.5 border text-sm font-semibold ${form.payment_method === k ? "border-coral bg-coral-light" : "border-border"}`} data-testid={`meal-pay-${k}`}>{l}</button>
                  ))}
                </div>
              </div>

              <div className="mt-4 bg-surface rounded-xl p-3 text-sm">
                <div className="flex justify-between"><span className="text-ink-600">Per day</span><span className="font-semibold">{inr(daily)}</span></div>
                <div className="flex justify-between"><span className="text-ink-600">GST / Tax</span><span className="text-ink-600">No GST — Enjoy bro</span></div>
                <div className="flex justify-between border-t border-border mt-1.5 pt-1.5 font-bold"><span>Total ({form.duration_days} days)</span><span className="text-coral">{inr(total)}</span></div>
              </div>

              <button onClick={subscribe} disabled={busy} className="w-full bg-coral text-white font-bold py-3 rounded-xl mt-3 flex items-center justify-center gap-2 tap" data-testid="meal-subscribe-btn">
                {busy ? <Loader2 size={16} className="animate-spin" /> : null} {form.payment_method === "cod" ? "Start plan · Pay daily" : `Pay ${inr(total)} & start`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, color, label, value }) {
  return <div className="bg-surface rounded-xl p-2 text-center"><Icon size={15} className={`mx-auto ${color}`} /><div className="font-bold text-ink text-sm mt-0.5">{value}</div><div className="text-[10px] text-ink-600">{label}</div></div>;
}
